import { Container, Graphics } from 'pixi.js'
import { Renderer } from '../engine/Renderer'
import { Input, InputEvent } from '../engine/Input'
import { Audio } from '../engine/Audio'
import { ParticleSystem } from '../engine/Particles'
import { GameMap } from './Map'
import { Unit } from './Unit'
import { Building } from './Building'
import { Entity } from './Entity'
import { Faction, FACTIONS, getFaction } from '../data/factions'
import { UNIT_TYPES, getUnitType } from '../data/units'
import { BUILDING_TYPES, getBuildingType } from '../data/buildings'
import { TERRAIN, getTerrainById } from '../data/terrain'
import { Vector2 } from '../utils/math'

export interface Resources {
  compute: number
  data: number
  energy: number
}

export interface GameConfig {
  mapWidth: number
  mapHeight: number
  mapSeed: number
  tileSize: number
  playerFaction: string
  enemyFaction: string
}

export type GamePhase = 'menu' | 'playing' | 'paused' | 'victory' | 'defeat'

export class Game {
  renderer: Renderer
  input: Input
  audio: Audio
  particles: ParticleSystem
  map: GameMap

  // Configuration
  config: GameConfig
  playerFaction: Faction
  enemyFaction: Faction

  // Game state
  phase: GamePhase = 'menu'
  resources: Resources = { compute: 500, data: 200, energy: 100 }
  population: number = 0
  populationCap: number = 10

  // Entities
  playerUnits: Unit[] = []
  playerBuildings: Building[] = []
  enemyUnits: Unit[] = []
  enemyBuildings: Building[] = []

  // Selection
  selectedUnits: Unit[] = []
  selectedBuilding: Building | null = null
  buildMode: string | null = null

  // UI callbacks
  onResourceChange?: (resources: Resources) => void
  onSelectionChange?: (units: Unit[], building: Building | null) => void
  onMessage?: (message: string, type: 'info' | 'success' | 'alert') => void
  onPhaseChange?: (phase: GamePhase) => void
  onPopulationChange?: (pop: number, cap: number) => void
  onGameOver?: (isVictory: boolean) => void

  private selectionBox: Graphics

  constructor(renderer: Renderer, input: Input, audio: Audio, config: GameConfig) {
    this.renderer = renderer
    this.input = input
    this.audio = audio
    this.config = config

    this.playerFaction = getFaction(config.playerFaction)
    this.enemyFaction = getFaction(config.enemyFaction)

    // Create map
    this.map = new GameMap({
      width: config.mapWidth,
      height: config.mapHeight,
      seed: config.mapSeed,
      tileSize: config.tileSize
    })

    // Create particle system
    this.particles = new ParticleSystem(renderer.layers.effects)

    // Create selection box graphic
    this.selectionBox = new Graphics()
    renderer.layers.ui.addChild(this.selectionBox)

    // Set up input handlers
    this.setupInputHandlers()

    renderer.setTileSize(config.tileSize)
  }

  private setupInputHandlers(): void {
    // Left click
    this.input.on('leftClick', (e: InputEvent) => {
      if (this.phase !== 'playing') return

      if (this.buildMode) {
        this.handleBuildPlacement(e.worldX!, e.worldY!)
      } else {
        this.handleSelection(e.worldX!, e.worldY!, e.shiftKey || false)
      }
    })

    // Left drag end (box selection)
    this.input.on('leftDragEnd', (e: InputEvent) => {
      if (this.phase !== 'playing') return
      if (this.buildMode) return

      const box = this.input.getSelectionBox()
      const dragDist = Math.sqrt(
        Math.pow(box.x2 - box.x1, 2) + Math.pow(box.y2 - box.y1, 2)
      )

      if (dragDist > 10) {
        this.handleBoxSelection(box.x1, box.y1, box.x2, box.y2, e.shiftKey || false)
      }
    })

    // Right click (command)
    this.input.on('rightClick', (e: InputEvent) => {
      if (this.phase !== 'playing') return

      if (this.buildMode) {
        this.buildMode = null
        this.audio.playError()
      } else {
        this.issueCommand(e.worldX!, e.worldY!)
      }
    })

    // Scroll (pinch-to-zoom) - zoom towards mouse position
    this.input.on('scroll', (e: InputEvent) => {
      const zoomDelta = e.delta! > 0 ? -0.1 : 0.1
      this.renderer.setCameraZoom(
        this.renderer.camera.targetZoom + zoomDelta,
        false,
        e.worldX,
        e.worldY
      )
    })

    // Pan (two-finger trackpad scroll)
    this.input.on('pan', (e: InputEvent) => {
      const panSpeed = 1 / this.renderer.camera.zoom  // Pan faster when zoomed out
      this.renderer.setCameraPosition(
        this.renderer.camera.targetX + (e.deltaX || 0) * panSpeed,
        this.renderer.camera.targetY + (e.deltaY || 0) * panSpeed
      )
    })

    // Keyboard
    this.input.on('keyDown', (e: InputEvent) => {
      if (e.key === 'escape') {
        this.buildMode = null
        this.clearSelection()
      }
      if (e.key === ' ') {
        if (this.phase === 'playing') {
          this.phase = 'paused'
        } else if (this.phase === 'paused') {
          this.phase = 'playing'
        }
        this.onPhaseChange?.(this.phase)
      }
    })
  }

  // Initialize game state
  start(): void {
    // Create player starting units and building
    const playerBase = this.createBuilding('DATACENTER', 8, 8, true)
    this.createUnit('WORKER', 6 * this.config.tileSize + 24, 12 * this.config.tileSize + 24, true)
    this.createUnit('WORKER', 8 * this.config.tileSize + 24, 12 * this.config.tileSize + 24, true)
    this.createUnit('WORKER', 10 * this.config.tileSize + 24, 12 * this.config.tileSize + 24, true)

    // Create enemy starting units and building
    const enemyX = this.config.mapWidth - 12
    const enemyY = this.config.mapHeight - 12
    this.createBuilding('DATACENTER', enemyX, enemyY, false)
    this.createUnit('WORKER', (enemyX - 2) * this.config.tileSize + 24, (enemyY + 4) * this.config.tileSize + 24, false)
    this.createUnit('WORKER', enemyX * this.config.tileSize + 24, (enemyY + 4) * this.config.tileSize + 24, false)
    this.createUnit('WARRIOR', (enemyX + 2) * this.config.tileSize + 24, (enemyY + 4) * this.config.tileSize + 24, false)

    // Center camera on player base
    this.renderer.setCameraPosition(
      playerBase.x,
      playerBase.y,
      true
    )

    this.phase = 'playing'
    this.onPhaseChange?.(this.phase)
    this.onMessage?.('Simulation initialized. Build your civilization.', 'success')

    this.audio.startAmbientMusic()
  }

  // Main update loop
  update(dt: number): void {
    if (this.phase !== 'playing') return

    // Reset fog visibility
    this.map.resetVisibility()

    // Update camera
    const edgeScroll = this.input.getEdgeScrollDelta(dt)
    const keyScroll = this.input.getKeyboardCameraDelta(dt)
    this.renderer.panCamera(edgeScroll.dx + keyScroll.dx, edgeScroll.dy + keyScroll.dy)
    this.renderer.clampCamera(this.config.mapWidth, this.config.mapHeight)
    this.renderer.updateCamera(dt)

    // Update entities
    this.updateUnits(this.playerUnits, dt)
    this.updateUnits(this.enemyUnits, dt)
    this.updateBuildings(this.playerBuildings, dt)
    this.updateBuildings(this.enemyBuildings, dt)

    // Update particles
    this.particles.update(dt)

    // Resource generation from buildings
    this.updateResourceGeneration(dt)

    // Update fog of war from player units and buildings
    this.updateFogOfWar()

    // Check victory conditions
    this.checkVictoryConditions()

    // Update enemy AI
    this.updateEnemyAI(dt)

    // Update game intensity for music
    const combatUnits = this.playerUnits.filter(u => u.state === 'attacking').length
    this.audio.setGameIntensity(Math.min(1, combatUnits / 5))
  }

  // Enemy AI state
  private enemyResources: Resources = { compute: 500, data: 200, energy: 100 }
  private enemyAITimer: number = 0
  private enemyAIInterval: number = 3  // AI makes decisions every 3 seconds
  private enemyAttackTimer: number = 0
  private enemyAttackInterval: number = 60  // Attack player every 60 seconds

  private updateEnemyAI(dt: number): void {
    this.enemyAITimer += dt
    this.enemyAttackTimer += dt

    // Generate enemy resources from buildings
    for (const building of this.enemyBuildings) {
      if (!building.isConstructed) continue
      const type = building.buildingType
      if (type.generates) {
        if (type.generates.compute) this.enemyResources.compute += type.generates.compute * dt
        if (type.generates.data) this.enemyResources.data += type.generates.data * dt
        if (type.generates.energy) this.enemyResources.energy += type.generates.energy * dt
      }
    }

    // AI decision making
    if (this.enemyAITimer >= this.enemyAIInterval) {
      this.enemyAITimer = 0
      this.makeEnemyDecision()
    }

    // Periodic attack
    if (this.enemyAttackTimer >= this.enemyAttackInterval && this.enemyUnits.length >= 3) {
      this.enemyAttackTimer = 0
      this.launchEnemyAttack()
    }

    // Enemy workers gather resources
    for (const unit of this.enemyUnits) {
      if (unit.unitType.canGather && unit.state === 'idle' && unit.carrying === 0) {
        this.assignEnemyWorkerToGather(unit)
      }
    }
  }

  private makeEnemyDecision(): void {
    // Get enemy datacenter
    const datacenter = this.enemyBuildings.find(b => b.buildingType.id === 'DATACENTER')
    if (!datacenter) return

    // Count units
    const workers = this.enemyUnits.filter(u => u.unitType.id === 'WORKER').length
    const warriors = this.enemyUnits.filter(u => u.unitType.id === 'WARRIOR').length

    // Calculate enemy population
    let enemyPopCap = 0
    for (const b of this.enemyBuildings) {
      if (b.buildingType.provides?.populationCap) {
        enemyPopCap += b.buildingType.provides.populationCap
      }
    }
    const enemyPop = this.enemyUnits.length

    // Decision: Train workers if we have few
    if (workers < 3 && datacenter.trainingQueue.length === 0) {
      const workerCost = getUnitType('WORKER').cost
      if (this.canEnemyAfford(workerCost)) {
        datacenter.queueUnit('WORKER', getUnitType('WORKER').buildTime)
        this.deductEnemyResources(workerCost)
        return
      }
    }

    // Decision: Build barracks if we don't have one
    const barracks = this.enemyBuildings.find(b => b.buildingType.id === 'BARRACKS')
    if (!barracks && this.canEnemyAfford(getBuildingType('BARRACKS').cost)) {
      const baseX = datacenter.tileX
      const baseY = datacenter.tileY
      // Try to place barracks near base
      for (let dy = -3; dy <= 3; dy++) {
        for (let dx = -3; dx <= 3; dx++) {
          const tx = baseX + dx
          const ty = baseY + dy
          if (this.map.canPlaceBuilding(tx, ty, 2)) {
            this.createBuilding('BARRACKS', tx, ty, false)
            this.deductEnemyResources(getBuildingType('BARRACKS').cost)
            return
          }
        }
      }
    }

    // Decision: Train warriors
    const activeBarracks = this.enemyBuildings.find(b => b.buildingType.id === 'BARRACKS' && b.isConstructed)
    if (activeBarracks && warriors < 6 && enemyPop < enemyPopCap) {
      const warriorCost = getUnitType('WARRIOR').cost
      if (this.canEnemyAfford(warriorCost) && activeBarracks.trainingQueue.length < 2) {
        activeBarracks.queueUnit('WARRIOR', getUnitType('WARRIOR').buildTime)
        this.deductEnemyResources(warriorCost)
      }
    }

    // Decision: Build more data centers for population
    if (enemyPop >= enemyPopCap - 2 && this.canEnemyAfford(getBuildingType('DATACENTER').cost)) {
      const baseX = datacenter.tileX
      const baseY = datacenter.tileY
      for (let dy = -5; dy <= 5; dy++) {
        for (let dx = -5; dx <= 5; dx++) {
          const tx = baseX + dx
          const ty = baseY + dy
          if (this.map.canPlaceBuilding(tx, ty, 2)) {
            this.createBuilding('DATACENTER', tx, ty, false)
            this.deductEnemyResources(getBuildingType('DATACENTER').cost)
            return
          }
        }
      }
    }
  }

  private launchEnemyAttack(): void {
    // Get warriors to attack
    const attackers = this.enemyUnits.filter(u => u.unitType.attack > 5 && u.state === 'idle')
    if (attackers.length < 2) return

    // Find player building to attack
    if (this.playerBuildings.length === 0) return
    const target = this.playerBuildings[Math.floor(Math.random() * this.playerBuildings.length)]

    for (const attacker of attackers) {
      attacker.attackTarget(target)
    }
  }

  private assignEnemyWorkerToGather(worker: Unit): void {
    // Find nearest resource tile
    const tileX = Math.floor(worker.x / this.config.tileSize)
    const tileY = Math.floor(worker.y / this.config.tileSize)

    let bestTile: { x: number; y: number; resource: 'compute' | 'data' | 'energy' } | null = null
    let bestDist = Infinity

    // Search in a radius around the worker
    for (let dy = -10; dy <= 10; dy++) {
      for (let dx = -10; dx <= 10; dx++) {
        const tx = tileX + dx
        const ty = tileY + dy
        if (tx < 0 || ty < 0 || tx >= this.config.mapWidth || ty >= this.config.mapHeight) continue

        const terrain = getTerrainById(this.map.terrain[ty]?.[tx])
        if (terrain?.resource) {
          const dist = Math.abs(dx) + Math.abs(dy)
          if (dist < bestDist) {
            bestDist = dist
            bestTile = {
              x: tx * this.config.tileSize + this.config.tileSize / 2,
              y: ty * this.config.tileSize + this.config.tileSize / 2,
              resource: terrain.resource
            }
          }
        }
      }
    }

    if (bestTile) {
      worker.gatherFrom(bestTile.x, bestTile.y, bestTile.resource)
    }
  }

  private canEnemyAfford(cost: { compute?: number; data?: number; energy?: number }): boolean {
    if (cost.compute && this.enemyResources.compute < cost.compute) return false
    if (cost.data && this.enemyResources.data < cost.data) return false
    if (cost.energy && this.enemyResources.energy < cost.energy) return false
    return true
  }

  private deductEnemyResources(cost: { compute?: number; data?: number; energy?: number }): void {
    if (cost.compute) this.enemyResources.compute -= cost.compute
    if (cost.data) this.enemyResources.data -= cost.data
    if (cost.energy) this.enemyResources.energy -= cost.energy
  }

  private updateUnits(units: Unit[], dt: number): void {
    for (let i = units.length - 1; i >= 0; i--) {
      const unit = units[i]
      unit.update(dt)

      // Handle resource returning
      if (unit.state === 'returning' && unit.carrying > 0) {
        this.handleResourceReturn(unit, dt)
      }

      // Auto-combat for idle combat units
      if (unit.state === 'idle' && unit.unitType.attack > 0) {
        this.handleAutoCombat(unit)
      }

      if (unit.isDead) {
        this.removeUnit(unit)
      }
    }
  }

  // Handle workers returning resources to base
  private handleResourceReturn(unit: Unit, dt: number): void {
    const buildings = unit.isPlayer ? this.playerBuildings : this.enemyBuildings

    // Find nearest building that can receive resources (Data Center or similar)
    let nearestBuilding: Building | null = null
    let nearestDist = Infinity

    for (const building of buildings) {
      if (!building.isConstructed) continue
      // Any building can receive resources for now
      const dist = unit.distanceTo(building)
      if (dist < nearestDist) {
        nearestDist = dist
        nearestBuilding = building
      }
    }

    if (!nearestBuilding) {
      unit.state = 'idle'
      return
    }

    // Check if close enough to deposit
    const depositRange = nearestBuilding.buildingType.size * this.config.tileSize / 2 + 30
    if (nearestDist <= depositRange) {
      // Deposit resources
      const deposited = unit.depositResources()
      if (deposited && unit.isPlayer) {
        if (deposited.type === 'compute') this.resources.compute += deposited.amount
        else if (deposited.type === 'data') this.resources.data += deposited.amount
        else if (deposited.type === 'energy') this.resources.energy += deposited.amount

        this.onResourceChange?.(this.resources)
        this.particles.emitSpark(unit.x, unit.y, 0x2ecc71)
      }

      // Go back to gathering if we have a previous gather target stored
      const lastGatherTarget = unit.getLastGatherTarget()
      if (lastGatherTarget) {
        const terrain = getTerrainById(this.map.terrain[Math.floor(lastGatherTarget.y / this.config.tileSize)]?.[Math.floor(lastGatherTarget.x / this.config.tileSize)])
        if (terrain?.resource) {
          unit.gatherFrom(lastGatherTarget.x, lastGatherTarget.y, terrain.resource)
        } else {
          unit.state = 'idle'
        }
      } else {
        unit.state = 'idle'
      }
    } else {
      // Move toward building
      const dx = nearestBuilding.x - unit.x
      const dy = nearestBuilding.y - unit.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist > 0) {
        const moveSpeed = unit.speed * 48 * dt
        unit.x += (dx / dist) * Math.min(moveSpeed, dist)
        unit.y += (dy / dist) * Math.min(moveSpeed, dist)
      }
    }
  }

  // Auto-attack nearby enemies when idle
  private handleAutoCombat(unit: Unit): void {
    const enemies = unit.isPlayer ? this.enemyUnits : this.playerUnits
    const enemyBuildings = unit.isPlayer ? this.enemyBuildings : this.playerBuildings

    const sightRange = unit.sight * this.config.tileSize
    let nearestTarget: Entity | null = null
    let nearestDist = sightRange

    // Check enemy units first
    for (const enemy of enemies) {
      const dist = unit.distanceTo(enemy)
      if (dist < nearestDist) {
        nearestDist = dist
        nearestTarget = enemy
      }
    }

    // Check enemy buildings
    for (const building of enemyBuildings) {
      const dist = unit.distanceTo(building)
      if (dist < nearestDist) {
        nearestDist = dist
        nearestTarget = building
      }
    }

    if (nearestTarget) {
      unit.attackTarget(nearestTarget)
    }
  }

  private updateBuildings(buildings: Building[], dt: number): void {
    for (let i = buildings.length - 1; i >= 0; i--) {
      const building = buildings[i]
      building.update(dt)

      // Check for completed training
      if (building.isConstructed && building.trainingQueue.length > 0) {
        const current = building.trainingQueue[0]
        if (current.progress >= 100) {
          this.handleTrainingComplete(building)
        }
      }

      // Tower attacks
      if (building.canAttack()) {
        this.handleTowerAttack(building)
      }

      if (building.isDead) {
        this.removeBuilding(building)
      }
    }
  }

  private updateResourceGeneration(dt: number): void {
    for (const building of this.playerBuildings) {
      if (!building.isConstructed) continue

      const type = building.buildingType
      if (type.generates) {
        if (type.generates.compute) {
          this.resources.compute += type.generates.compute * dt
        }
        if (type.generates.data) {
          this.resources.data += type.generates.data * dt
        }
        if (type.generates.energy) {
          this.resources.energy += type.generates.energy * dt
        }
        this.onResourceChange?.(this.resources)
      }
    }
  }

  private updateFogOfWar(): void {
    for (const unit of this.playerUnits) {
      this.map.revealArea(unit.x, unit.y, unit.sight)
    }
    for (const building of this.playerBuildings) {
      this.map.revealArea(building.x, building.y, 6)
    }
  }

  private handleTrainingComplete(building: Building): void {
    const current = building.trainingQueue[0]
    const unitType = getUnitType(current.unitType)

    // Check population cap
    const maxPop = Math.floor(this.populationCap * (this.playerFaction.bonuses.populationCap || 1))
    if (this.population >= maxPop) {
      this.onMessage?.('Population cap reached!', 'alert')
      return
    }

    // Spawn unit at rally point or find a walkable spot near building
    let spawnX: number = building.x
    let spawnY: number = building.y + building.buildingType.size * this.config.tileSize / 2 + this.config.tileSize

    if (building.rallyPoint) {
      spawnX = building.rallyPoint.x
      spawnY = building.rallyPoint.y
    } else {
      // Find a walkable tile adjacent to the building
      const buildingSize = building.buildingType.size
      const tileSize = this.config.tileSize
      const baseTileX = building.tileX
      const baseTileY = building.tileY

      // Try tiles below, then right, then left, then above the building
      const candidates = [
        { tx: baseTileX + Math.floor(buildingSize / 2), ty: baseTileY + buildingSize },     // below center
        { tx: baseTileX + buildingSize, ty: baseTileY + Math.floor(buildingSize / 2) },     // right center
        { tx: baseTileX - 1, ty: baseTileY + Math.floor(buildingSize / 2) },               // left center
        { tx: baseTileX + Math.floor(buildingSize / 2), ty: baseTileY - 1 },               // above center
      ]

      for (const { tx, ty } of candidates) {
        if (tx >= 0 && ty >= 0 && tx < this.map.width && ty < this.map.height) {
          const grid = this.map.getPathGrid()
          if (grid.isWalkable(tx, ty)) {
            spawnX = tx * tileSize + tileSize / 2
            spawnY = ty * tileSize + tileSize / 2
            break
          }
        }
      }
    }

    this.createUnit(current.unitType, spawnX, spawnY, building.isPlayer)
    building.trainingQueue.shift()

    this.audio.playComplete()
    this.onMessage?.(`${unitType.name} ready.`, 'success')
  }

  private handleTowerAttack(tower: Building): void {
    const enemies = tower.isPlayer ? this.enemyUnits : this.playerUnits
    const range = tower.getAttackRange()

    let nearest: Unit | null = null
    let nearestDist = Infinity

    for (const enemy of enemies) {
      const dist = tower.distanceTo(enemy)
      if (dist <= range && dist < nearestDist) {
        nearest = enemy
        nearestDist = dist
      }
    }

    if (nearest) {
      const damage = tower.performAttack()
      nearest.takeDamage(damage)
      this.particles.emitSpark(nearest.x, nearest.y, tower.faction.primaryColor)
      this.audio.playAttack()
    }
  }

  // Rendering
  render(): void {
    const bounds = this.renderer.getVisibleBounds()

    // Render map layers
    this.map.renderTerrain(this.renderer.layers.terrain, bounds)
    this.map.renderGrid(this.renderer.layers.terrain, bounds)
    this.map.renderFog(this.renderer.layers.fog, bounds)

    // Render entities
    this.renderEntities()

    // Render selection box
    this.renderSelectionBox()

    // Render build preview
    if (this.buildMode) {
      this.renderBuildPreview()
    }
  }

  private renderEntities(): void {
    // Update unit/building graphics
    for (const unit of [...this.playerUnits, ...this.enemyUnits]) {
      unit.render()
      if (!unit.container.parent) {
        this.renderer.layers.units.addChild(unit.container)
      }
    }

    for (const building of [...this.playerBuildings, ...this.enemyBuildings]) {
      building.render()
      if (!building.container.parent) {
        this.renderer.layers.buildings.addChild(building.container)
      }
    }
  }

  private renderSelectionBox(): void {
    this.selectionBox.clear()

    if (this.input.selection.active) {
      const box = this.input.getSelectionBox()
      const start = this.renderer.worldToScreen(box.x1, box.y1)
      const end = this.renderer.worldToScreen(box.x2, box.y2)

      const width = end.x - start.x
      const height = end.y - start.y

      this.selectionBox.rect(start.x, start.y, width, height)
      this.selectionBox.fill({ color: 0x00ff00, alpha: 0.1 })
      this.selectionBox.stroke({ width: 1, color: 0x00ff00 })
    }
  }

  private renderBuildPreview(): void {
    const type = getBuildingType(this.buildMode!)
    if (!type) return

    const tileX = Math.floor(this.input.mouse.worldX / this.config.tileSize)
    const tileY = Math.floor(this.input.mouse.worldY / this.config.tileSize)

    const canBuild = this.map.canPlaceBuilding(tileX, tileY, type.size, type.requiresTerrain) &&
                     this.canAfford(type.cost)

    const screenPos = this.renderer.worldToScreen(
      tileX * this.config.tileSize,
      tileY * this.config.tileSize
    )
    const size = type.size * this.config.tileSize * this.renderer.camera.zoom

    // Draw preview using selectionBox graphics
    this.selectionBox.rect(screenPos.x, screenPos.y, size, size)
    this.selectionBox.fill({ color: canBuild ? 0x00ff00 : 0xff0000, alpha: 0.3 })
    this.selectionBox.stroke({ width: 2, color: canBuild ? 0x00ff00 : 0xff0000 })
  }

  // Entity creation
  createUnit(typeId: string, x: number, y: number, isPlayer: boolean): Unit {
    const faction = isPlayer ? this.config.playerFaction : this.config.enemyFaction
    const unit = new Unit(typeId, x, y, isPlayer, faction)

    // Set up attack callback for effects
    unit.onAttack = (attacker, target) => {
      this.particles.emitSpark(target.x, target.y, attacker.faction.primaryColor)
      this.audio.playAttack()
    }

    // Set up pathfinding callback for combat movement
    unit.onNeedPath = (u, targetX, targetY) => {
      const path = this.map.findPath(u.x, u.y, targetX, targetY)
      if (path.length > 0) {
        u.path = path
      }
    }

    if (isPlayer) {
      this.playerUnits.push(unit)
      this.population++
      this.onPopulationChange?.(this.population, this.populationCap)
    } else {
      this.enemyUnits.push(unit)
    }

    this.renderer.layers.units.addChild(unit.container)
    return unit
  }

  createBuilding(typeId: string, tileX: number, tileY: number, isPlayer: boolean, startConstructed: boolean = true): Building {
    const faction = isPlayer ? this.config.playerFaction : this.config.enemyFaction
    const building = new Building(typeId, tileX, tileY, this.config.tileSize, isPlayer, faction, startConstructed)

    // Occupy map tiles
    this.map.occupyTiles(tileX, tileY, building.buildingType.size)

    // Update population cap
    if (isPlayer && building.buildingType.provides?.populationCap) {
      this.populationCap += building.buildingType.provides.populationCap
      this.onPopulationChange?.(this.population, this.populationCap)
    }

    if (isPlayer) {
      this.playerBuildings.push(building)
    } else {
      this.enemyBuildings.push(building)
    }

    this.renderer.layers.buildings.addChild(building.container)
    return building
  }

  // Entity removal
  private removeUnit(unit: Unit): void {
    const list = unit.isPlayer ? this.playerUnits : this.enemyUnits
    const index = list.indexOf(unit)
    if (index !== -1) {
      list.splice(index, 1)
    }

    // Remove from selection
    const selIndex = this.selectedUnits.indexOf(unit)
    if (selIndex !== -1) {
      this.selectedUnits.splice(selIndex, 1)
      this.onSelectionChange?.(this.selectedUnits, this.selectedBuilding)
    }

    if (unit.isPlayer) {
      this.population--
      this.onPopulationChange?.(this.population, this.populationCap)
    }

    // Death effects
    this.particles.emitDeath(unit.x, unit.y, unit.faction.primaryColor)
    this.audio.playDeath()

    unit.destroy()
  }

  private removeBuilding(building: Building): void {
    const list = building.isPlayer ? this.playerBuildings : this.enemyBuildings
    const index = list.indexOf(building)
    if (index !== -1) {
      list.splice(index, 1)
    }

    // Free map tiles
    this.map.freeTiles(building.tileX, building.tileY, building.buildingType.size)

    // Update population cap
    if (building.isPlayer && building.buildingType.provides?.populationCap) {
      this.populationCap -= building.buildingType.provides.populationCap
      this.onPopulationChange?.(this.population, this.populationCap)
    }

    if (this.selectedBuilding === building) {
      this.selectedBuilding = null
      this.onSelectionChange?.(this.selectedUnits, null)
    }

    // Death effects
    this.particles.emitExplosion(building.x, building.y, building.faction.primaryColor, 2)
    this.audio.playDeath()

    building.destroy()
  }

  // Selection
  private handleSelection(worldX: number, worldY: number, additive: boolean): void {
    // Check for unit click
    for (const unit of this.playerUnits) {
      if (unit.distanceToPoint(worldX, worldY) < 20) {
        if (additive) {
          if (this.selectedUnits.includes(unit)) {
            unit.setSelected(false)
            this.selectedUnits = this.selectedUnits.filter(u => u !== unit)
          } else {
            unit.setSelected(true)
            this.selectedUnits.push(unit)
          }
        } else {
          this.clearSelection()
          unit.setSelected(true)
          this.selectedUnits = [unit]
        }
        this.selectedBuilding = null
        this.audio.playSelect()
        this.onSelectionChange?.(this.selectedUnits, null)
        return
      }
    }

    // Check for building click
    for (const building of this.playerBuildings) {
      const size = building.buildingType.size * this.config.tileSize
      if (worldX >= building.x - size / 2 && worldX <= building.x + size / 2 &&
          worldY >= building.y - size / 2 && worldY <= building.y + size / 2) {
        this.clearSelection()
        building.setSelected(true)
        this.selectedBuilding = building
        this.audio.playSelect()
        this.onSelectionChange?.([], building)
        return
      }
    }

    // Nothing clicked, clear selection
    if (!additive) {
      this.clearSelection()
    }
  }

  private handleBoxSelection(x1: number, y1: number, x2: number, y2: number, additive: boolean): void {
    const newSelection: Unit[] = []

    for (const unit of this.playerUnits) {
      if (unit.x >= x1 && unit.x <= x2 && unit.y >= y1 && unit.y <= y2) {
        newSelection.push(unit)
      }
    }

    if (newSelection.length > 0) {
      if (!additive) {
        this.clearSelection()
      }

      for (const unit of newSelection) {
        if (!this.selectedUnits.includes(unit)) {
          unit.setSelected(true)
          this.selectedUnits.push(unit)
        }
      }

      this.selectedBuilding = null
      this.audio.playSelect()
      this.onSelectionChange?.(this.selectedUnits, null)
    }
  }

  clearSelection(): void {
    for (const unit of this.selectedUnits) {
      unit.setSelected(false)
    }
    this.selectedUnits = []

    if (this.selectedBuilding) {
      this.selectedBuilding.setSelected(false)
      this.selectedBuilding = null
    }

    this.onSelectionChange?.([], null)
  }

  // Commands
  private issueCommand(worldX: number, worldY: number): void {
    if (this.selectedUnits.length === 0) return

    // Check for enemy target
    const enemyUnit = this.findEnemyAtPoint(worldX, worldY)
    const enemyBuilding = this.findEnemyBuildingAtPoint(worldX, worldY)

    // Check for resource
    const tileX = Math.floor(worldX / this.config.tileSize)
    const tileY = Math.floor(worldY / this.config.tileSize)
    const terrain = getTerrainById(this.map.terrain[tileY]?.[tileX])
    const isResource = terrain?.resource !== undefined

    for (let i = 0; i < this.selectedUnits.length; i++) {
      const unit = this.selectedUnits[i]

      if (enemyUnit) {
        unit.attackTarget(enemyUnit)
      } else if (enemyBuilding) {
        unit.attackTarget(enemyBuilding)
      } else if (isResource && unit.unitType.canGather) {
        const resourceX = tileX * this.config.tileSize + this.config.tileSize / 2
        const resourceY = tileY * this.config.tileSize + this.config.tileSize / 2
        unit.gatherFrom(resourceX, resourceY, terrain!.resource!)
      } else {
        // Move command with formation offset
        const offset = i * 30
        const angle = (i / this.selectedUnits.length) * Math.PI * 2
        const targetX = worldX + Math.cos(angle) * offset
        const targetY = worldY + Math.sin(angle) * offset

        const path = this.map.findPath(unit.x, unit.y, targetX, targetY)
        unit.moveTo(path)
      }
    }

    this.audio.playClick()
  }

  private findEnemyAtPoint(worldX: number, worldY: number): Unit | null {
    for (const enemy of this.enemyUnits) {
      if (enemy.distanceToPoint(worldX, worldY) < 20) {
        return enemy
      }
    }
    return null
  }

  private findEnemyBuildingAtPoint(worldX: number, worldY: number): Building | null {
    for (const building of this.enemyBuildings) {
      const size = building.buildingType.size * this.config.tileSize
      const inX = worldX >= building.x - size / 2 && worldX <= building.x + size / 2
      const inY = worldY >= building.y - size / 2 && worldY <= building.y + size / 2
      console.log(`Check building at (${building.x}, ${building.y}), size=${size}, click=(${worldX}, ${worldY}), inX=${inX}, inY=${inY}`)
      if (inX && inY) {
        console.log('Found enemy building!')
        return building
      }
    }
    console.log(`No enemy building found at (${worldX}, ${worldY}), checked ${this.enemyBuildings.length} buildings`)
    return null
  }

  // Building placement
  private handleBuildPlacement(worldX: number, worldY: number): void {
    if (!this.buildMode) return

    const type = getBuildingType(this.buildMode)
    if (!type) return

    const tileX = Math.floor(worldX / this.config.tileSize)
    const tileY = Math.floor(worldY / this.config.tileSize)

    if (!this.map.canPlaceBuilding(tileX, tileY, type.size, type.requiresTerrain)) {
      this.onMessage?.('Cannot build here!', 'alert')
      this.audio.playError()
      return
    }

    if (!this.canAfford(type.cost)) {
      this.onMessage?.('Insufficient resources!', 'alert')
      this.audio.playError()
      return
    }

    // Deduct cost
    this.payCost(type.cost)

    // Create building (under construction)
    const building = this.createBuilding(this.buildMode, tileX, tileY, true, false)

    // Start construction progress (auto-completes over time for now)
    const buildInterval = setInterval(() => {
      building.advanceConstruction(2)
      this.particles.emitConstruction(building.x, building.y)

      if (building.isConstructed) {
        this.onMessage?.(`${type.name} complete!`, 'success')
        this.audio.playComplete()
        clearInterval(buildInterval)
      }
    }, 100)

    this.audio.playBuild()
    this.buildMode = null
  }

  // Start building mode
  startBuildMode(buildingType: string): void {
    const type = getBuildingType(buildingType)
    if (!type) return

    // Check if any selected workers can build
    const hasWorker = this.selectedUnits.some(u => u.unitType.canBuild)
    if (!hasWorker && this.selectedUnits.length > 0) {
      this.onMessage?.('Select a worker to build!', 'alert')
      return
    }

    this.buildMode = buildingType
  }

  // Training
  trainUnit(building: Building, unitType: string): boolean {
    const type = getUnitType(unitType)
    if (!type) return false

    // Apply faction cost modifier
    const costMod = this.playerFaction.bonuses.unitCost || 1
    const cost = {
      compute: Math.floor((type.cost.compute || 0) * costMod),
      data: Math.floor((type.cost.data || 0) * costMod),
      energy: Math.floor((type.cost.energy || 0) * costMod)
    }

    if (!this.canAfford(cost)) {
      this.onMessage?.('Insufficient resources!', 'alert')
      this.audio.playError()
      return false
    }

    // Check population cap
    const maxPop = Math.floor(this.populationCap * (this.playerFaction.bonuses.populationCap || 1))
    if (this.population >= maxPop) {
      this.onMessage?.('Population cap reached!', 'alert')
      this.audio.playError()
      return false
    }

    this.payCost(cost)

    const success = building.queueUnit(unitType, type.buildTime)
    if (success) {
      this.audio.playClick()
      this.onMessage?.(`Training ${type.name}...`, 'info')
    }

    return success
  }

  // Resources
  canAfford(cost: { compute?: number; data?: number; energy?: number }): boolean {
    if (cost.compute && this.resources.compute < cost.compute) return false
    if (cost.data && this.resources.data < cost.data) return false
    if (cost.energy && this.resources.energy < cost.energy) return false
    return true
  }

  private payCost(cost: { compute?: number; data?: number; energy?: number }): void {
    if (cost.compute) this.resources.compute -= cost.compute
    if (cost.data) this.resources.data -= cost.data
    if (cost.energy) this.resources.energy -= cost.energy
    this.onResourceChange?.(this.resources)
  }

  addResources(type: 'compute' | 'data' | 'energy', amount: number): void {
    this.resources[type] += amount
    this.onResourceChange?.(this.resources)
  }

  // Victory conditions
  private checkVictoryConditions(): void {
    // Check if player has no buildings
    if (this.playerBuildings.length === 0) {
      this.phase = 'defeat'
      this.onPhaseChange?.(this.phase)
      this.onMessage?.('DEFEAT - Your civilization has fallen!', 'alert')
      this.audio.playDefeat()
      this.audio.stopAmbientMusic()
      this.onGameOver?.(false)
      return
    }

    // Check if enemy has no buildings
    if (this.enemyBuildings.length === 0 && this.enemyUnits.length === 0) {
      this.phase = 'victory'
      this.onPhaseChange?.(this.phase)
      this.onMessage?.('VICTORY - The simulation is yours!', 'success')
      this.audio.playVictory()
      this.audio.stopAmbientMusic()
      this.onGameOver?.(true)
      return
    }

    // Check for wonder victory
    const playerWonder = this.playerBuildings.find(b => b.buildingType.isWonder && b.isConstructed)
    if (playerWonder) {
      this.phase = 'victory'
      this.onPhaseChange?.(this.phase)
      this.onMessage?.('SINGULARITY ACHIEVED - Transcendence complete!', 'success')
      this.audio.playVictory()
      this.audio.stopAmbientMusic()
      this.onGameOver?.(true)
    }
  }

  destroy(): void {
    this.particles.destroy()
    this.map.destroy()

    for (const unit of [...this.playerUnits, ...this.enemyUnits]) {
      unit.destroy()
    }
    for (const building of [...this.playerBuildings, ...this.enemyBuildings]) {
      building.destroy()
    }

    this.audio.destroy()
  }
}
